import { useRef, useState } from 'react';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { ArrowLeft, Flag, MapPin, Plus, Search } from 'lucide-react';
import { getJobSites, createJobSite, updateJobSite, removeJobSiteFlag } from '../services/jobSiteApi';
import { getCustomersPaged, getCustomerLocations } from '../services/customerApi';
import { useDebounce } from '@hooks/useDebounce';
import ConfirmationModal from '@shared/components/common/ConfirmationModal';
import ResizablePanel from '@shared/components/common/ResizablePanel';
import { withXmId } from '@shared/utils/xmId';
import { getInitials } from '@shared/utils/stringUtils';
import { US_STATES } from '@shared/constants/us-data';
import type { Customer, CustomerLocation } from '../types/clientClinic';
import type { CreateJobSiteRequest, JobSite } from '../types/jobSite';
import './CustomerLocationBrowserPage.css';
import './JobSitesPage.css';

// Customer names may use "Last, First" formatting, so split on commas too.
const CUSTOMER_NAME_SEPARATOR = /[\s,]+/;

// On-scroll pagination page size for the customer list.
const CUSTOMER_PAGE_SIZE = 50;

const EMPTY_FORM = {
  alias: '',
  code: '',
  modelLocationId: 0,
  copyServiceProfiles: true,
  mailingStreet: '',
  mailingSuite: '',
  mailingCity: '',
  mailingState: '',
  mailingZip: '',
  mainPhone: '',
};

/** Surface the API's { message } body when present, else a fallback. */
function getApiErrorMessage(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const message = (error.response?.data as { message?: string } | undefined)?.message;
    if (message) return message;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * JobSites page (SLV2-2470 pattern re-expressed): pick a customer, then list,
 * create, and promote its JobSite-flagged locations. A JobSite behaves like a
 * normal location everywhere except it is excluded from Navision sync; billing
 * routes to the model location it was created from. The model location is
 * optional (SLV2-4124): without one, billing goes to the customer and the
 * JobSite cannot be used on orders (SLV2-4125).
 */
export default function JobSitesPage() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [view, setView] = useState<'list' | 'create' | 'edit'>('list');
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editTarget, setEditTarget] = useState<JobSite | null>(null);
  const [removeTarget, setRemoveTarget] = useState<JobSite | null>(null);
  const customerListRef = useRef<HTMLDivElement>(null);

  // Server-side search + pagination: the customer table is larger than any
  // sane single fetch, so each page (and the search) comes from the API.
  const debouncedSearch = useDebounce(searchQuery, 300);
  const {
    data: customerPages,
    isLoading: isLoadingCustomers,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['customers', 'paged', debouncedSearch],
    queryFn: ({ pageParam }) => getCustomersPaged(debouncedSearch, pageParam, CUSTOMER_PAGE_SIZE),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.meta.page < last.meta.totalPages ? last.meta.page + 1 : undefined),
  });

  const customers = customerPages?.pages.flatMap((p) => p.data) ?? [];
  const totalCustomers = customerPages?.pages[0]?.meta.totalCount ?? 0;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // New search -> the query key changes and pagination restarts server-side.
    setSearchQuery(e.target.value);
    customerListRef.current?.scrollTo({ top: 0 });
  };

  const handleCustomerListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (hasNextPage && !isFetchingNextPage && scrollTop + clientHeight >= scrollHeight - 200) {
      void fetchNextPage();
    }
  };

  const {
    data: jobSites = [],
    isLoading: isLoadingJobSites,
    isError: isJobSitesError,
    error: jobSitesError,
    refetch: refetchJobSites,
  } = useQuery<JobSite[]>({
    queryKey: ['jobSites', selectedCustomer?.id],
    queryFn: () => getJobSites(selectedCustomer!.id),
    enabled: !!selectedCustomer,
  });

  // Model-location choices: the customer's locations minus existing JobSites
  // (a JobSite is modeled after a normal peer location).
  const { data: locations = [] } = useQuery<CustomerLocation[]>({
    queryKey: ['customerLocations', selectedCustomer?.id],
    queryFn: () => getCustomerLocations(selectedCustomer!.id),
    enabled: !!selectedCustomer,
  });
  const jobSiteIds = new Set(jobSites.map((j) => j.id));
  const modelChoices = locations.filter((l) => !jobSiteIds.has(l.id));
  const hasModelLocation = formData.modelLocationId > 0;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['jobSites', selectedCustomer?.id] });
    queryClient.invalidateQueries({ queryKey: ['customerLocations', selectedCustomer?.id] });
  };

  const createMutation = useMutation({
    mutationFn: () => {
      const request: CreateJobSiteRequest = {
        alias: formData.alias.trim(),
        code: formData.code.trim() || undefined,
        modelLocationId: hasModelLocation ? formData.modelLocationId : undefined,
        copyServiceProfiles: hasModelLocation && formData.copyServiceProfiles,
        mailingStreet: formData.mailingStreet.trim() || undefined,
        mailingSuite: formData.mailingSuite.trim() || undefined,
        mailingCity: formData.mailingCity.trim(),
        mailingState: formData.mailingState,
        mailingZip: formData.mailingZip.trim(),
        mailingCountry: 'US',
        mainPhone: formData.mainPhone.trim() || undefined,
      };
      return createJobSite(selectedCustomer!.id, request);
    },
    onSuccess: () => {
      invalidate();
      setFormData(EMPTY_FORM);
      setView('list');
    },
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      updateJobSite(selectedCustomer!.id, editTarget!.id, {
        alias: formData.alias.trim(),
        code: formData.code.trim() || undefined,
        mailingStreet: formData.mailingStreet.trim() || undefined,
        mailingSuite: formData.mailingSuite.trim() || undefined,
        mailingCity: formData.mailingCity.trim(),
        mailingState: formData.mailingState,
        mailingZip: formData.mailingZip.trim(),
        mailingCountry: 'US',
        mainPhone: formData.mainPhone.trim() || undefined,
      }),
    onSuccess: () => {
      invalidate();
      setFormData(EMPTY_FORM);
      setEditTarget(null);
      setView('list');
    },
  });

  const removeMutation = useMutation({
    mutationFn: (locationId: number) => removeJobSiteFlag(selectedCustomer!.id, locationId),
    onSuccess: () => {
      invalidate();
      setRemoveTarget(null);
    },
  });

  const handleEditClick = (site: JobSite) => {
    updateMutation.reset();
    setEditTarget(site);
    setFormData({
      ...EMPTY_FORM,
      alias: site.alias ?? '',
      code: site.code ?? '',
      mailingStreet: site.mailingStreet ?? '',
      mailingSuite: site.mailingSuite ?? '',
      mailingCity: site.mailingCity ?? '',
      mailingState: site.mailingState ?? '',
      mailingZip: site.mailingZip ?? '',
      mainPhone: site.mainPhone ?? '',
    });
    setView('edit');
  };

  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    setView('list');
    setEditTarget(null);
    setRemoveTarget(null);
    setFormData(EMPTY_FORM);
    createMutation.reset();
    updateMutation.reset();
    removeMutation.reset();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  // SLV2-4124: the model location is optional, like Code and Street (SLV2-3641).
  const isFormValid =
    formData.alias.trim() !== '' &&
    formData.mailingCity.trim() !== '' &&
    formData.mailingState !== '' &&
    formData.mailingZip.trim() !== '';

  const activeMutation = view === 'edit' ? updateMutation : createMutation;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;
    activeMutation.mutate();
  };

  const handleBackToList = () => {
    createMutation.reset();
    updateMutation.reset();
    setEditTarget(null);
    setFormData(EMPTY_FORM);
    setView('list');
  };

  const formatAddress = (site: JobSite) =>
    site.mailingCity && site.mailingState
      ? `${site.mailingStreet ?? ''} ${site.mailingCity}, ${site.mailingState} ${site.mailingZip ?? ''}`.trim()
      : 'No address';

  // Left panel: Customer list with search + on-scroll pagination
  const leftPanel = (
    <div className="customer-list-panel">
      <div className="customer-list-header">
        <div className="customer-list-title-row">
          <span className="customer-list-title">Customers</span>
          {!isLoadingCustomers && (
            <span className="customer-list-count">{totalCustomers}</span>
          )}
        </div>
        <div className="search-box">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder="Search customers..."
            aria-label="Search customers"
            value={searchQuery}
            onChange={handleSearchChange}
            className="search-input"
          />
        </div>
      </div>

      <div className="customer-list" ref={customerListRef} onScroll={handleCustomerListScroll}>
        {isLoadingCustomers && (
          <div className="loading-state">
            <p>Loading customers...</p>
          </div>
        )}

        {!isLoadingCustomers && customers.length === 0 && (
          <div className="empty-state">
            <p>
              {searchQuery.length >= 1
                ? `No customers found matching "${searchQuery}"`
                : 'No customers available'}
            </p>
          </div>
        )}

        {!isLoadingCustomers && customers.map((customer) => {
          const isSelected = selectedCustomer?.id === customer.id;
          return (
            <button
              key={customer.id}
              type="button"
              className={`customer-card${isSelected ? ' selected' : ''}`}
              onClick={() => handleCustomerSelect(customer)}
              aria-pressed={isSelected}
            >
              <span className="customer-avatar" aria-hidden="true">
                {getInitials(customer.name, CUSTOMER_NAME_SEPARATOR) || '—'}
              </span>
              <span className="customer-info">
                <span className="customer-name">{withXmId(customer.name, customer.xmId)}</span>
                {customer.accountNumber && (
                  <span className="customer-account">{customer.accountNumber}</span>
                )}
              </span>
            </button>
          );
        })}

        {!isLoadingCustomers && hasNextPage && (
          <div className="customer-list-more" aria-live="polite">
            {isFetchingNextPage
              ? 'Loading more...'
              : `Showing ${customers.length} of ${totalCustomers} — scroll for more`}
          </div>
        )}
      </div>
    </div>
  );

  // Right panel: JobSites for the selected customer (list or create form)
  const rightPanel = (
    <div className="location-grid-panel">
      <div className="panel-header">
        <div className="panel-header-text">
          <h2>{selectedCustomer ? withXmId(selectedCustomer.name, selectedCustomer.xmId) : 'JobSites'}</h2>
          <p className="panel-header-subtitle">
            {selectedCustomer
              ? view === 'create'
                ? 'New JobSite — optionally modeled after an existing location'
                : view === 'edit'
                  ? `Editing "${editTarget?.alias ?? ''}" — billing routing and the JobSite flag are unchanged`
                  : `${jobSites.length} ${jobSites.length === 1 ? 'JobSite' : 'JobSites'} — transient work sites excluded from Navision sync`
              : 'Pick a customer from the list to manage their JobSites'}
          </p>
        </div>
        {selectedCustomer && view === 'list' && (
          <button type="button" className="btn-primary" onClick={() => setView('create')}>
            <Plus size={16} /> Add JobSite
          </button>
        )}
      </div>

      <div className="location-grid">
        {!selectedCustomer && (
          <div className="empty-state">
            <div className="empty-icon-wrap">
              <Flag size={36} className="empty-icon" />
            </div>
            <h3>No customer selected</h3>
            <p>Pick a customer from the list on the left to view and manage their JobSites.</p>
          </div>
        )}

        {selectedCustomer && view === 'list' && (
          <>
            {isLoadingJobSites && (
              <div className="loading-state">
                <p>Loading JobSites...</p>
              </div>
            )}

            {isJobSitesError && (
              <div className="error job-sites-error" role="alert">
                <p>{getApiErrorMessage(jobSitesError, 'Failed to load JobSites.')}</p>
                <button type="button" className="secondary btn-small" onClick={() => refetchJobSites()}>
                  Retry
                </button>
              </div>
            )}

            {!isLoadingJobSites && !isJobSitesError && jobSites.length === 0 && (
              <div className="empty-state">
                <div className="empty-icon-wrap">
                  <MapPin size={36} className="empty-icon" />
                </div>
                <h3>No JobSites</h3>
                <p>This customer has no JobSites yet. Use "Add JobSite" to create one.</p>
              </div>
            )}

            {!isLoadingJobSites && !isJobSitesError && jobSites.length > 0 && (
              <div className="location-table-wrapper">
                <table className="location-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Code</th>
                      <th>Address</th>
                      <th>Billing Location</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobSites.map((site) => (
                      <tr key={site.id}>
                        <td className="location-name">{site.alias}</td>
                        <td>{site.code ?? '—'}</td>
                        <td className="location-address">{formatAddress(site)}</td>
                        <td>
                          <span className="job-site-billing-cell">
                            {site.billingLocationName ?? site.billingContextId ?? '—'}
                            {/* Only an explicit false: an API without the field must not flag every JobSite. */}
                            {site.hasModelLocation === false && (
                              <span
                                className="job-site-badge job-site-badge-not-orderable"
                                title="No model location — billing goes to the customer and this JobSite can't be used on orders"
                              >
                                Not orderable
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="text-right">
                          <div className="job-site-row-actions">
                            <button
                              type="button"
                              className="secondary btn-small"
                              title="Edit this JobSite's details"
                              onClick={() => handleEditClick(site)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="secondary btn-small"
                              title="Remove the JobSite flag — the location becomes a normal location and resumes Navision sync"
                              onClick={() => {
                                removeMutation.reset();
                                setRemoveTarget(site);
                              }}
                            >
                              Remove Flag
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {selectedCustomer && (view === 'create' || view === 'edit') && (
          <form className="job-site-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="jobSiteAlias">Name *</label>
                <input
                  id="jobSiteAlias"
                  name="alias"
                  value={formData.alias}
                  onChange={handleChange}
                  required
                  maxLength={35}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label htmlFor="jobSiteCode">Code</label>
                <input
                  id="jobSiteCode"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  maxLength={15}
                />
              </div>
            </div>

            {view === 'create' && (
            <div className="form-group">
              <label htmlFor="jobSiteModelLocation">Model Location</label>
              <select
                id="jobSiteModelLocation"
                name="modelLocationId"
                value={formData.modelLocationId}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, modelLocationId: Number(e.target.value) }))
                }
                aria-describedby="jobSiteModelLocationHint"
              >
                <option value={0}>None — not used on orders</option>
                {modelChoices.map((location) => (
                  <option key={location.id} value={location.id}>
                    {withXmId(location.name, location.xmId)}
                    {location.city && location.state ? ` — ${location.city}, ${location.state}` : ''}
                  </option>
                ))}
              </select>
              <p className="field-hint" id="jobSiteModelLocationHint">
                {hasModelLocation
                  ? 'Billing routes to the model location; it must have a zip code on file.'
                  : "Optional. Without a model location, billing goes to the customer and this JobSite can't be used on orders."}
              </p>
            </div>
            )}

            {view === 'create' && (
            <div className="form-group checkbox">
              <label htmlFor="jobSiteCopyProfiles">
                <input
                  id="jobSiteCopyProfiles"
                  name="copyServiceProfiles"
                  type="checkbox"
                  checked={hasModelLocation && formData.copyServiceProfiles}
                  onChange={handleChange}
                  disabled={!hasModelLocation}
                  aria-describedby="jobSiteCopyProfilesHint"
                />
                Copy service profiles from model location
              </label>
              <p className="field-hint" id="jobSiteCopyProfilesHint">
                {hasModelLocation
                  ? 'Recommended — makes the JobSite order-ready immediately.'
                  : 'Available once a model location is selected.'}
              </p>
            </div>
            )}

            <div className="form-group">
              <label htmlFor="jobSiteStreet">Street</label>
              <input
                id="jobSiteStreet"
                name="mailingStreet"
                value={formData.mailingStreet}
                onChange={handleChange}
                maxLength={50}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="jobSiteSuite">Suite</label>
                <input
                  id="jobSiteSuite"
                  name="mailingSuite"
                  value={formData.mailingSuite}
                  onChange={handleChange}
                  maxLength={50}
                />
              </div>
              <div className="form-group">
                <label htmlFor="jobSiteCity">City *</label>
                <input
                  id="jobSiteCity"
                  name="mailingCity"
                  value={formData.mailingCity}
                  onChange={handleChange}
                  required
                  maxLength={50}
                />
              </div>
            </div>

            <div className="form-row form-row-3">
              <div className="form-group">
                <label htmlFor="jobSiteState">State *</label>
                <select
                  id="jobSiteState"
                  name="mailingState"
                  value={formData.mailingState}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select...</option>
                  {US_STATES.map((state) => (
                    <option key={state.value} value={state.value}>
                      {state.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="jobSiteZip">Zip *</label>
                <input
                  id="jobSiteZip"
                  name="mailingZip"
                  value={formData.mailingZip}
                  onChange={handleChange}
                  required
                  maxLength={5}
                  pattern="[0-9]{5}"
                />
              </div>
              <div className="form-group">
                <label htmlFor="jobSitePhone">Phone</label>
                <input
                  id="jobSitePhone"
                  name="mainPhone"
                  value={formData.mainPhone}
                  onChange={handleChange}
                  maxLength={10}
                />
              </div>
            </div>

            {activeMutation.isError && (
              <div className="error job-sites-error" role="alert">
                <p>
                  {getApiErrorMessage(
                    activeMutation.error,
                    view === 'edit' ? 'Failed to save the JobSite.' : 'Failed to create the JobSite.'
                  )}
                </p>
              </div>
            )}

            <div className="job-site-form-actions">
              <button
                type="button"
                className="secondary"
                onClick={handleBackToList}
                disabled={activeMutation.isPending}
              >
                <ArrowLeft size={16} /> Back
              </button>
              <button type="submit" className="btn-primary" disabled={activeMutation.isPending || !isFormValid}>
                {view === 'edit'
                  ? activeMutation.isPending ? 'Saving...' : 'Save Changes'
                  : activeMutation.isPending ? 'Creating...' : 'Create JobSite'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );

  return (
    <div className="customer-location-browser-page">
      <div className="page-header">
        <div className="page-title-row">
          <Flag size={28} />
          <h1>JobSites</h1>
        </div>
        <p className="page-description">
          Create and manage transient work sites — JobSites behave like normal locations but are excluded from Navision sync
        </p>
      </div>

      <ResizablePanel
        leftPanel={leftPanel}
        rightPanel={rightPanel}
        defaultLeftWidth={350}
        minLeftWidth={300}
        maxLeftWidth={500}
        storageKey="job-sites-panel-width"
      />

      <ConfirmationModal
        isOpen={!!removeTarget}
        title="Remove JobSite flag?"
        message={`"${removeTarget?.alias ?? ''}" will be promoted to a normal customer location. It keeps all its data and will resume syncing to Navision on its next update.`}
        confirmText={removeMutation.isPending ? 'Removing...' : 'Remove Flag'}
        danger
        loading={removeMutation.isPending}
        error={
          removeMutation.isError
            ? getApiErrorMessage(removeMutation.error, 'Failed to remove the JobSite flag.')
            : null
        }
        onConfirm={() => removeTarget && removeMutation.mutate(removeTarget.id)}
        onCancel={() => {
          setRemoveTarget(null);
          removeMutation.reset();
        }}
      />
    </div>
  );
}
